import { compileWebhookLookup, parseWebhookLookup } from './compile/webhook-lookup.js';
import { makeBehaviorPassthrough } from './compile/behavior-passthrough.js';
import { makeRouteEffect } from './compile/route-effect.js';
import { makeConfigCheck, validateCheck, crossFieldCheck, webhookCheck } from './compile/check-effect.js';
import { rowCheck } from './compile/row-check-effect.js';
/**
 * Registry data. validMoments/defaultMoment/validScopes/defaultScope were
 * VERIFIED against the real runtime sites in the R0 truth pass (E-062,
 * 2026-08-10) — see the R0 table in
 * docs/superpowers/plans/2026-08-10-e062-authoring-redesign.md and the
 * per-entry evidence comments below. The authoring UI derives its constraint
 * layer from THIS metadata only; the registry⇄runtime parity tripwire
 * (apps/api/src/services/__tests__/registry-runtime-parity.test.ts, P6
 * pattern) fails CI if a moment claim drifts from the eval sites.
 *
 * Runtime sites for content ACTION rules (route bucket): 'on-step-enter'
 * (advanceDocument), 'on-validate' at the accept advance (R2), 'on-export'
 * (releaseBatch — batch/hybrid manual release only), 'on-schedule'
 * (on-schedule/tick.ts). The batch balance triggers have NO action eval site —
 * do not re-add batch_classified/batch_complete to a route effect without
 * building the runtime first.
 *
 * History: docs/audits/2026-07-07-automation-effect-parity-audit.md (P0).
 */
const fieldEngineRuleBackend = () => 'field_engine_rule';
const serverDataRuleBackend = () => 'server_data_rule';
const fieldBehaviorBackend = () => 'field_behavior';
const workflowActionBackend = () => 'workflow_action';
export const EFFECTS = [
    // --- Check bucket ---
    {
        key: 'validate_check',
        label: 'Validate',
        description: 'Check a field against a format, range, or required-value constraint at keying or validation time.',
        bucket: 'check',
        validMoments: ['keying', 'on_validate'],
        defaultMoment: 'on_validate',
        validScopes: ['field'],
        defaultScope: 'field',
        backendFor: fieldEngineRuleBackend,
        ...validateCheck,
    },
    {
        key: 'cross_field_check',
        label: 'Cross-field consistency',
        description: 'Verify that two or more fields on the same form (or batch) agree with each other before the document can proceed.',
        bucket: 'check',
        // R0 evidence: evaluated ONLY by the batch gate (run-content-batch-gate.ts
        // pass 3 form-scope / pass 4 batch-scope). The accept path
        // (run-content-validate.ts) has no form-rule pass, so on_validate was a
        // false claim — a crossField "check at accept" never fired there.
        validMoments: ['batch_complete'],
        defaultMoment: 'batch_complete',
        validScopes: ['form', 'batch'],
        defaultScope: 'form',
        backendFor: fieldEngineRuleBackend,
        ...crossFieldCheck,
    },
    {
        key: 'row_check',
        label: 'Check each row',
        description: 'Verify that every row of a line-item group satisfies a per-row invariant (e.g. each line amount is within the header total) — failing rows are pinpointed on the offending cell.',
        bucket: 'check',
        validMoments: ['on_validate', 'batch_complete'],
        defaultMoment: 'on_validate',
        validScopes: ['form'],
        defaultScope: 'form',
        backendFor: fieldEngineRuleBackend,
        ...rowCheck,
    },
    {
        key: 'unique_check',
        label: 'Must be unique',
        description: 'Reject a value that has already been seen elsewhere in the batch or in prior submissions.',
        bucket: 'check',
        validMoments: ['on_validate', 'batch_complete'],
        defaultMoment: 'batch_complete',
        validScopes: ['field'],
        defaultScope: 'field',
        backendFor: serverDataRuleBackend,
        ...makeConfigCheck('unique_check', 'uniqueness', 'batch_complete'),
    },
    {
        key: 'historical_range_check',
        label: 'Within historical range',
        description: 'Compare a value against the historical distribution for this field and flag statistical outliers.',
        bucket: 'check',
        validMoments: ['on_validate', 'batch_complete'],
        defaultMoment: 'batch_complete',
        validScopes: ['field'],
        defaultScope: 'field',
        backendFor: serverDataRuleBackend,
        ...makeConfigCheck('historical_range_check', 'historicalRange', 'batch_complete'),
    },
    {
        key: 'webhook_check',
        label: 'Check via webhook',
        description: 'Call an external service to validate a value (e.g. vendor lookup) — at the batch gate by default, or at an authored earlier timing (keying, accept, step entry) with a pending state (S-5 async check lane).',
        bucket: 'check',
        // R0 evidence: opted-in rules dial at their authored trigger —
        // VALID_WEBHOOK_TRIGGERS = on-change/on-validate/on-step-enter
        // (webhook-rules.ts, collectWebhookChecksForTiming) — and EVERY webhook
        // rule participates in the batch gate (run-content-batch-gate.ts pass 2b,
        // trigger-blind). Moment→trigger threading: compile/parse below.
        validMoments: ['keying', 'on_validate', 'step_enter', 'batch_complete'],
        defaultMoment: 'batch_complete',
        validScopes: ['field'],
        defaultScope: 'field',
        backendFor: serverDataRuleBackend,
        ...webhookCheck,
    },
    // --- Fill/transform bucket ---
    {
        key: 'compute',
        label: 'Compute',
        description: 'Derive a field value from an expression over other fields on the same document.',
        bucket: 'fill_transform',
        validMoments: ['pre_export'],
        defaultMoment: 'pre_export',
        validScopes: ['field'],
        defaultScope: 'field',
        backendFor: fieldBehaviorBackend,
        ...makeBehaviorPassthrough('compute', 'compute', 'pre_export'),
    },
    {
        key: 'substitute',
        label: 'Substitute / map values',
        description: 'Replace a raw captured value with a mapped value from a lookup table.',
        bucket: 'fill_transform',
        validMoments: ['pre_export'],
        defaultMoment: 'pre_export',
        validScopes: ['field'],
        defaultScope: 'field',
        backendFor: fieldBehaviorBackend,
        ...makeBehaviorPassthrough('substitute', 'substitute', 'pre_export'),
    },
    {
        key: 'transform',
        label: 'Reformat',
        description: 'Rewrite a value into a target format (e.g. date, currency, casing) before export.',
        bucket: 'fill_transform',
        validMoments: ['pre_export'],
        defaultMoment: 'pre_export',
        validScopes: ['field'],
        defaultScope: 'field',
        backendFor: fieldBehaviorBackend,
        ...makeBehaviorPassthrough('transform', 'transform', 'pre_export'),
    },
    {
        key: 'parse_split',
        label: 'Split into fields',
        description: 'Split one captured value into multiple derived fields using a delimiter or pattern.',
        bucket: 'fill_transform',
        validMoments: ['pre_export'],
        defaultMoment: 'pre_export',
        validScopes: ['field'],
        defaultScope: 'field',
        backendFor: fieldBehaviorBackend,
        ...makeBehaviorPassthrough('parse', 'parse_split', 'pre_export'),
    },
    {
        key: 'populate_source',
        label: 'Populate from source (OCR/header)',
        description: 'Fill a field automatically from OCR output or header/batch-level metadata.',
        bucket: 'fill_transform',
        validMoments: ['pre_export'],
        defaultMoment: 'pre_export',
        validScopes: ['field'],
        defaultScope: 'field',
        backendFor: fieldBehaviorBackend,
        ...makeBehaviorPassthrough('populate', 'populate_source', 'pre_export'),
    },
    {
        key: 'webhook_lookup',
        label: 'Look up via webhook',
        description: 'Call an external service and map its JSON response into derived fields.',
        bucket: 'fill_transform',
        validMoments: ['pre_export'],
        defaultMoment: 'pre_export',
        validScopes: ['field'],
        defaultScope: 'field',
        backendFor: fieldBehaviorBackend,
        compile: (s) => compileWebhookLookup(s),
        parse: (p) => parseWebhookLookup(p),
    },
    // --- Route bucket ---
    {
        key: 'hold',
        label: 'Hold',
        description: 'Pause the document in its current step pending manual release.',
        bucket: 'route',
        // R0 evidence: action rules evaluate at on-step-enter (advanceDocument),
        // on-export (releaseBatch), on-schedule (tick). The batch balance moments
        // were removed — no action eval site exists for them (runBalanceRules even
        // misread such rules with inverted polarity until R0b).
        validMoments: ['step_enter', 'pre_export', 'scheduled'],
        defaultMoment: 'step_enter',
        validScopes: ['batch', 'form'],
        defaultScope: 'batch',
        backendFor: workflowActionBackend,
        ...makeRouteEffect('hold'),
    },
    {
        key: 'escalate',
        label: 'Escalate',
        description: 'Route the document to a supervisor or higher-privilege queue for review.',
        bucket: 'route',
        // R0 evidence: action rules evaluate at on-step-enter (advanceDocument),
        // on-export (releaseBatch), on-schedule (tick). The batch balance moments
        // were removed — no action eval site exists for them (runBalanceRules even
        // misread such rules with inverted polarity until R0b).
        validMoments: ['step_enter', 'pre_export', 'scheduled'],
        defaultMoment: 'step_enter',
        validScopes: ['batch', 'form'],
        defaultScope: 'batch',
        backendFor: workflowActionBackend,
        ...makeRouteEffect('escalate'),
    },
    {
        key: 'reject',
        label: 'Reject',
        description: 'Reject the document out of the pipeline with a reason recorded on the audit trail.',
        bucket: 'route',
        // R0 evidence: action rules evaluate at on-step-enter (advanceDocument),
        // on-export (releaseBatch), on-schedule (tick). The batch balance moments
        // were removed — no action eval site exists for them (runBalanceRules even
        // misread such rules with inverted polarity until R0b).
        validMoments: ['step_enter', 'pre_export', 'scheduled'],
        defaultMoment: 'step_enter',
        validScopes: ['batch', 'form'],
        defaultScope: 'batch',
        backendFor: workflowActionBackend,
        ...makeRouteEffect('reject'),
    },
    {
        key: 'route_to_queue',
        label: 'Move to a pipeline step',
        description: "Send the document to a specific existing step in this job's pipeline instead of the default next step.",
        bucket: 'route',
        // R0 evidence: action rules evaluate at on-step-enter (advanceDocument),
        // on-export (releaseBatch), on-schedule (tick). The batch balance moments
        // were removed — no action eval site exists for them (runBalanceRules even
        // misread such rules with inverted polarity until R0b).
        validMoments: ['step_enter', 'pre_export', 'scheduled'],
        defaultMoment: 'step_enter',
        validScopes: ['batch', 'form'],
        defaultScope: 'batch',
        backendFor: workflowActionBackend,
        ...makeRouteEffect('route_to_queue'),
    },
    {
        key: 'skip_step',
        label: 'Skip a step',
        description: 'Bypass the next step in the linear pipeline when a condition is met.',
        bucket: 'route',
        // R0 evidence: action rules evaluate at on-step-enter (advanceDocument),
        // on-export (releaseBatch), on-schedule (tick). The batch balance moments
        // were removed — no action eval site exists for them (runBalanceRules even
        // misread such rules with inverted polarity until R0b).
        validMoments: ['step_enter', 'pre_export', 'scheduled'],
        defaultMoment: 'step_enter',
        validScopes: ['batch', 'form'],
        defaultScope: 'batch',
        backendFor: workflowActionBackend,
        ...makeRouteEffect('skip_step'),
    },
    {
        key: 'reclassify',
        label: 'Reclassify',
        description: 'Change the document/job type classification and re-route it through the matching pipeline.',
        bucket: 'route',
        // R0 evidence: action rules evaluate at on-step-enter (advanceDocument),
        // on-export (releaseBatch), on-schedule (tick). The batch balance moments
        // were removed — no action eval site exists for them (runBalanceRules even
        // misread such rules with inverted polarity until R0b).
        validMoments: ['step_enter', 'pre_export', 'scheduled'],
        defaultMoment: 'step_enter',
        validScopes: ['batch', 'form'],
        defaultScope: 'batch',
        backendFor: workflowActionBackend,
        ...makeRouteEffect('reclassify'),
    },
    {
        key: 'reprioritize',
        label: 'Change priority',
        description: "Raise or lower the document's processing priority when the condition is met (higher sorts to the front of the queue).",
        bucket: 'route',
        // R0 evidence: action rules evaluate at on-step-enter (advanceDocument),
        // on-export (releaseBatch), on-schedule (tick). The batch balance moments
        // were removed — no action eval site exists for them (runBalanceRules even
        // misread such rules with inverted polarity until R0b).
        validMoments: ['step_enter', 'pre_export', 'scheduled'],
        defaultMoment: 'step_enter',
        validScopes: ['batch', 'form'],
        defaultScope: 'form',
        backendFor: workflowActionBackend,
        ...makeRouteEffect('reprioritize'),
    },
    {
        key: 'notify',
        label: 'Notify',
        description: 'Notify a role — a persistent in-app notification plus a live alert when the condition is met.',
        bucket: 'route',
        // R0 evidence: same three action-rule moments as its siblings, PLUS
        // on_validate (R2): the accept advance (advanceDocument, trigger
        // 'post_edit') evaluates 'on-validate' action rules filtered by the
        // ACCEPTED step's stage — "notify when a document is accepted at X".
        validMoments: ['step_enter', 'on_validate', 'pre_export', 'scheduled'],
        defaultMoment: 'step_enter',
        validScopes: ['batch', 'form'],
        defaultScope: 'batch',
        backendFor: workflowActionBackend,
        ...makeRouteEffect('notify'),
    },
    {
        key: 'set_field',
        label: 'Stamp a value',
        // Rail-wide caveat (shared with every route effect at batch moments): at
        // pre_export/batch_classified/batch_complete the branch-IF evaluates ONCE
        // against the batch-level ctx and the resolved actions replay per document —
        // condition gating is all-or-nothing for the batch. The stamped VALUE is
        // still computed per document (the executor re-reads each doc's fields for
        // expression values). Per-doc condition gating at batch moments is a rail
        // change, not a set_field change.
        description: 'Write a value into a field when the condition is met — replace, append (for accumulating exception codes), or fill only when empty.',
        bucket: 'route',
        // R0 evidence: action rules evaluate at on-step-enter (advanceDocument),
        // on-export (releaseBatch), on-schedule (tick). The batch balance moments
        // were removed — no action eval site exists for them (runBalanceRules even
        // misread such rules with inverted polarity until R0b).
        validMoments: ['step_enter', 'pre_export', 'scheduled'],
        defaultMoment: 'step_enter',
        // Stamping is per-document by nature — default to form scope (batch still allowed).
        validScopes: ['batch', 'form'],
        defaultScope: 'form',
        backendFor: workflowActionBackend,
        ...makeRouteEffect('set_field'),
    },
];
export const RETIRED = [
    { key: 'custom-script', origin: 'rule_type', reason: 'legacy/inactive; rejected at publish gate' },
    {
        key: 'webhook_transform',
        origin: 'action_type',
        reason: 'dead stub — no runtime handler',
        replacedBy: 'webhook_lookup',
    },
    {
        key: 'default',
        origin: 'action_type',
        reason: 'dead action — no executor (closest live: populate literal, different moment)',
    },
    {
        key: 'substitute',
        origin: 'action_type',
        reason: 'dead action — no executor',
        replacedBy: 'substitute',
    },
    {
        key: 'format',
        origin: 'action_type',
        reason: 'dead action — no executor',
        replacedBy: 'transform',
    },
    {
        key: 'calculate',
        origin: 'action_type',
        reason: 'dead action — no executor',
        replacedBy: 'compute',
    },
    { key: 'copy', origin: 'action_type', reason: 'dead action — no live equivalent' },
    {
        key: 'lookup',
        origin: 'action_type',
        reason: 'dead action — no live equivalent (LookupAutoFillConfig has zero consumers)',
    },
    { key: 'dateBoundary', origin: 'action_type', reason: 'dead action — would fail-closed if wired' },
    {
        key: 'crossField',
        origin: 'action_type',
        reason: 'not authorable as an action; live only as a rule-type condition mechanism',
    },
    {
        key: 'expression',
        origin: 'action_type',
        reason: 'dead action — no standalone executor; expression lives as a condition/value source and as a set_field valueKind, not as an action',
        replacedBy: 'set_field (valueKind: expression)',
    },
    {
        key: 'substitute',
        origin: 'rule_type',
        reason: 'vestigial rule type — absent from ACTIVE_RULE_TYPES',
        replacedBy: 'substitute',
    },
    {
        key: 'format',
        origin: 'rule_type',
        reason: 'vestigial rule type',
        replacedBy: 'transform',
    },
    {
        key: 'calculate',
        origin: 'rule_type',
        reason: 'vestigial rule type',
        replacedBy: 'compute',
    },
    { key: 'copy', origin: 'rule_type', reason: 'vestigial rule type — no live equivalent' },
    {
        key: 'require_fields',
        origin: 'workflow_flag',
        reason: 'display-only — collected into flagState for verification-station UI visibility, NO runtime executor (intentional; the executor is a future item). The flag IS set (secondary type in the resolver) and surfaced to operators, but nothing acts on it automatically.',
    },
];
export const HOMED_ELSEWHERE = [
    {
        key: 'display',
        origin: 'behavior_kind',
        reason: 'unwired at runtime; real read-only is the separate FieldDefinition.readOnly',
        replacedBy: 'field editor Basics',
    },
    {
        key: 'emit',
        origin: 'behavior_kind',
        reason: 'always overwritten to a constant; real routing is in export mapping content',
        replacedBy: 'export mapping',
    },
];
export function effectByKey(key) {
    return EFFECTS.find((e) => e.key === key);
}
//# sourceMappingURL=effect-registry.js.map